import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Box,
  Typography,
  Paper,
  Button,
  ButtonBase,
  LinearProgress,
  Chip,
  CircularProgress,
  IconButton,
} from '@mui/material'
import { HowToVote, Timer, Backspace, Send, Lock, RestartAlt, LockOpen } from '@mui/icons-material'
import { VotingStateDto, SessionInputType } from '@api/types/session'
import { NumpadInputStyle } from '../../../types/workflow.types'
import { useSessionVoting } from '../../../hooks/useSessionVoting'
import { useFlickInertia } from '../../../hooks/useFlickInertia'

interface VotingPanelProps {
  voting: VotingStateDto
  sessionId: string | null
  questionText?: string | null
  challengeType?: SessionInputType | null
  challengeData?: Record<string, unknown> | null
}

const LETTER_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H']

export default function VotingPanel({ voting, sessionId, questionText, challengeType, challengeData }: VotingPanelProps) {
  const {
    castVote: rawCastVote,
    isVoting,
    hasVoted,
    userVote,
    options,
    votesCast,
    totalVoters,
    percentageVoted,
    getTimeRemaining,
  } = useSessionVoting(sessionId)

  const [submitted, setSubmitted] = useState(false)
  const [lastRoundId, setLastRoundId] = useState(voting.roundId)
  const [timeRemaining, setTimeRemaining] = useState<number | null>(getTimeRemaining())

  // Reset submitted state when a new voting round starts
  if (voting.roundId !== lastRoundId) {
    setLastRoundId(voting.roundId)
    setSubmitted(false)
  }

  // Wrap castVote to immediately mark as submitted
  const castVote = (value: string) => {
    setSubmitted(true)
    rawCastVote(value)
  }

  const showWaiting = hasVoted || submitted

  // Update countdown timer
  useEffect(() => {
    if (!voting.timerExpiresAt) return

    const interval = setInterval(() => {
      const remaining = getTimeRemaining()
      setTimeRemaining(remaining)
      if (remaining === 0) {
        clearInterval(interval)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [voting.timerExpiresAt, getTimeRemaining])

  // Format time remaining
  const formatTimeRemaining = (seconds: number | null): string => {
    if (seconds === null) return ''
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const isOpen = voting.status === 'Open'

  return (
    <Paper
      elevation={4}
      sx={{
        borderTop: 3,
        borderColor: 'primary.main',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HowToVote color="primary" />
          <Typography variant="h6">Group Vote</Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Vote progress */}
          <Chip
            label={`${votesCast}/${totalVoters} votes`}
            size="small"
            color={votesCast >= voting.requiredVotes ? 'success' : 'default'}
          />

          {/* Timer */}
          {timeRemaining !== null && timeRemaining > 0 && (
            <Chip
              icon={<Timer />}
              label={formatTimeRemaining(timeRemaining)}
              size="small"
              color={timeRemaining < 30 ? 'error' : 'default'}
            />
          )}
        </Box>
      </Box>

      {/* Progress bar */}
      <LinearProgress
        variant="determinate"
        value={percentageVoted}
        sx={{ height: 4 }}
      />

      {/* Voting content */}
      <Box sx={{ p: 2 }}>
        {showWaiting ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 1 }}>
            Your vote has been cast. Waiting for other participants...
          </Typography>
        ) : !isOpen ? (
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {voting.status === 'Resolved'
              ? 'Voting has concluded.'
              : 'Voting is closed.'}
          </Typography>
        ) : (challengeType === 'Numpad' || challengeType === 'NumericInput') ? (
          <NumpadVoteInput
            challengeData={challengeData}
            questionText={questionText}
            isVoting={isVoting}
            onSubmit={castVote}
          />
        ) : challengeType === 'Codex' ? (
          <CodexVoteInput
            challengeData={challengeData}
            questionText={questionText}
            isVoting={isVoting}
            onSubmit={castVote}
          />
        ) : (
          <QuestionAnswerVoteInput
            options={options}
            questionText={questionText}
            isVoting={isVoting}
            userVote={userVote}
            isOpen={isOpen}
            hasVoted={hasVoted}
            onSubmit={castVote}
          />
        )}
      </Box>
    </Paper>
  )
}


// ========================
// QuestionAnswer Vote Input
// ========================

interface QAVoteInputProps {
  options: VotingStateDto['options']
  questionText?: string | null
  isVoting: boolean
  userVote: string | null | undefined
  isOpen: boolean
  hasVoted: boolean
  onSubmit: (value: string) => void
}

function QuestionAnswerVoteInput({ options, questionText, isVoting, userVote, isOpen, hasVoted, onSubmit }: QAVoteInputProps) {
  const [selectedOption, setSelectedOption] = useState<string | null>(userVote ?? null)

  useEffect(() => {
    if (userVote) setSelectedOption(userVote)
  }, [userVote])

  const canSubmit = selectedOption !== null && isOpen && !hasVoted

  const handleOptionSelect = (optionId: string) => {
    if (!isOpen || hasVoted || isVoting) return
    setSelectedOption(optionId)
  }

  const handleSubmit = () => {
    if (!selectedOption || !isOpen || hasVoted) return
    onSubmit(selectedOption)
  }

  return (
    <>
      {questionText && (
        <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.6, color: 'text.secondary', mb: 1.5 }}>
          {questionText}
        </Typography>
      )}

      <Typography
        sx={{
          fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase',
          letterSpacing: '0.8px', color: 'text.disabled', mb: 1.5,
          display: 'flex', alignItems: 'center', gap: 0.75,
        }}
      >
        ✍ Choose one answer
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.75 }}>
        {options.map((option, index) => {
          const isSelected = selectedOption === option.id
          return (
            <ButtonBase
              key={option.id}
              onClick={() => handleOptionSelect(option.id)}
              disabled={!isOpen || isVoting || hasVoted}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                bgcolor: isSelected
                  ? (theme) => theme.palette.mode === 'dark' ? 'rgba(212,168,83,0.08)' : 'rgba(212,168,83,0.06)'
                  : (theme) => theme.palette.mode === 'dark' ? 'background.default' : 'background.paper',
                border: 1.5, borderColor: isSelected ? 'primary.main' : 'divider',
                borderRadius: 3, px: 2, py: 1.75, width: '100%', textAlign: 'left',
                transition: 'all 0.15s',
                '&:hover': {
                  borderColor: isSelected ? 'primary.main' : (theme) => theme.palette.mode === 'dark' ? 'rgba(212,168,83,0.3)' : 'rgba(0,0,0,0.2)',
                  bgcolor: isSelected ? undefined : (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                },
              }}
            >
              <Box sx={{
                width: 22, height: 22, borderRadius: '50%', border: 2,
                borderColor: isSelected ? 'primary.main' : 'text.disabled',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, transition: 'all 0.15s',
              }}>
                <Box sx={{
                  width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main',
                  transform: isSelected ? 'scale(1)' : 'scale(0)', transition: 'transform 0.15s',
                }} />
              </Box>
              <Typography sx={{ flex: 1, fontSize: '0.875rem', fontWeight: 500, color: 'text.primary' }}>
                {option.label}
              </Typography>
              <Box sx={{
                width: 24, height: 24, borderRadius: 1.5, flexShrink: 0,
                bgcolor: isSelected
                  ? (theme) => theme.palette.mode === 'dark' ? 'rgba(212,168,83,0.1)' : 'rgba(212,168,83,0.08)'
                  : 'rgba(128,128,128,0.08)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: isSelected ? 'primary.main' : 'text.disabled' }}>
                  {LETTER_LABELS[index] || ''}
                </Typography>
              </Box>
            </ButtonBase>
          )
        })}
      </Box>

      <ButtonBase
        onClick={handleSubmit}
        disabled={!canSubmit || isVoting}
        sx={{
          width: '100%',
          background: canSubmit ? 'linear-gradient(135deg, #d4a853, #c4943f)' : 'rgba(128,128,128,0.2)',
          border: 'none', borderRadius: 3, color: canSubmit ? '#1a1a2e' : 'text.disabled',
          fontSize: '0.9375rem', fontWeight: 700, py: 1.75, fontFamily: "'Inter', sans-serif",
          boxShadow: canSubmit ? '0 2px 10px rgba(212,168,83,0.3)' : 'none',
          transition: 'all 0.15s',
          '&:hover': canSubmit ? { boxShadow: '0 4px 16px rgba(212,168,83,0.4)' } : {},
        }}
      >
        {isVoting ? <CircularProgress size={20} sx={{ color: '#1a1a2e' }} /> : 'Submit Vote'}
      </ButtonBase>
    </>
  )
}


// ========================
// Numpad Vote Input
// ========================

interface NumpadVoteInputProps {
  challengeData?: Record<string, unknown> | null
  questionText?: string | null
  isVoting: boolean
  onSubmit: (value: string) => void
}

function NumpadVoteInput({ challengeData, questionText, isVoting, onSubmit }: NumpadVoteInputProps) {
  const digitCount = (challengeData?.digitCount ?? challengeData?.DigitCount ?? 4) as number
  const inputStyle = (challengeData?.inputStyle ?? challengeData?.InputStyle ?? 0) as NumpadInputStyle
  const [code, setCode] = useState('')

  const handleKeyPress = (digit: string) => {
    if (code.length < digitCount) {
      setCode((prev) => prev + digit)
    }
  }

  const handleBackspace = () => {
    setCode((prev) => prev.slice(0, -1))
  }

  const handleSubmit = () => {
    if (code.length !== digitCount || isVoting) return
    onSubmit(code)
  }

  const canSubmit = code.length === digitCount

  // Free input style
  if (inputStyle === NumpadInputStyle.FreeInput) {
    return (
      <>
        {questionText && (
          <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.6, color: 'text.secondary', mb: 1.5 }}>
            {questionText}
          </Typography>
        )}
        <Typography sx={{
          fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase',
          letterSpacing: '0.8px', color: 'text.disabled', mb: 1.5,
          display: 'flex', alignItems: 'center', gap: 0.75,
        }}>
          🔢 Enter Number
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            component="input"
            type="text"
            inputMode="numeric"
            value={code}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const val = e.target.value.replace(/\D/g, '').slice(0, digitCount)
              setCode(val)
            }}
            onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' && canSubmit) handleSubmit() }}
            placeholder="Enter number..."
            disabled={isVoting}
            sx={{
              flex: 1, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(21,32,58,0.6)' : 'rgba(0,0,0,0.04)',
              border: 1, borderColor: 'divider', borderRadius: '24px', px: 2.5, py: 1.5,
              fontSize: '1.125rem', fontWeight: 600, color: 'text.primary',
              fontFamily: "'Inter', sans-serif", textAlign: 'center', letterSpacing: '2px',
              fontVariantNumeric: 'tabular-nums', outline: 'none',
              '&:focus': { borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(212,168,83,0.4)' : 'primary.main' },
              '&::placeholder': { color: 'text.disabled', fontWeight: 400, letterSpacing: 0, fontSize: '0.875rem' },
            }}
          />
          <ButtonBase
            onClick={handleSubmit}
            disabled={!canSubmit || isVoting}
            sx={{
              width: 46, height: 46, borderRadius: '50%',
              background: canSubmit ? 'linear-gradient(135deg, #d4a853, #c4943f)' : 'rgba(128,128,128,0.2)',
              color: canSubmit ? '#1a1a2e' : 'text.disabled', flexShrink: 0,
              boxShadow: canSubmit ? '0 2px 8px rgba(212,168,83,0.3)' : 'none', transition: 'all 0.15s',
            }}
          >
            {isVoting ? <CircularProgress size={20} sx={{ color: '#1a1a2e' }} /> : <Send sx={{ fontSize: 18 }} />}
          </ButtonBase>
        </Box>
      </>
    )
  }

  // Roller digits style
  if (inputStyle === NumpadInputStyle.RollerDigits) {
    return (
      <RollerDigitsVoteInput
        digitCount={digitCount}
        questionText={questionText}
        isVoting={isVoting}
        onSubmit={onSubmit}
      />
    )
  }

  // Default 10-key pad style
  return (
    <>
      {questionText && (
        <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.6, color: 'text.secondary', mb: 1.5 }}>
          {questionText}
        </Typography>
      )}
      <Typography sx={{
        fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.8px', color: 'text.disabled', mb: 1.5,
        display: 'flex', alignItems: 'center', gap: 0.75,
      }}>
        🔢 Enter Code
      </Typography>

      {/* Code display */}
      <Box sx={{
        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(21,32,58,0.6)' : 'rgba(0,0,0,0.04)',
        border: 1, borderColor: 'divider', borderRadius: 3, px: 2.5, py: 1.75, mb: 1.75,
        textAlign: 'center', fontVariantNumeric: 'tabular-nums',
      }}>
        <Typography sx={{
          fontSize: '2rem', fontWeight: 700, letterSpacing: '12px', color: 'text.primary',
          fontFamily: "'Inter', sans-serif", display: 'inline',
        }}>
          {code}
        </Typography>
        <Typography component="span" sx={{
          fontSize: '2rem', fontWeight: 700, letterSpacing: '12px', color: 'text.disabled', opacity: 0.3,
        }}>
          {'●'.repeat(Math.max(0, digitCount - code.length))}
        </Typography>
        {code.length < digitCount && (
          <Box component="span" sx={{
            display: 'inline-block', width: 2, height: 28, bgcolor: 'primary.main',
            verticalAlign: 'middle', ml: 0.5, animation: 'blink 1s step-end infinite',
            '@keyframes blink': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0 } },
          }} />
        )}
      </Box>

      {/* Numpad grid */}
      <Box sx={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1,
        maxWidth: 280, mx: 'auto', mb: 1.25,
      }}>
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
          <ButtonBase
            key={digit}
            onClick={() => handleKeyPress(digit)}
            disabled={code.length >= digitCount || isVoting}
            sx={{
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : 'background.paper',
              border: 1, borderColor: 'divider', borderRadius: 3, color: 'text.primary',
              fontSize: '1.375rem', fontWeight: 600, py: 1.75, fontFamily: "'Inter', sans-serif",
              transition: 'all 0.1s',
              '&:active': {
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                transform: 'scale(0.96)',
              },
              '&.Mui-disabled': { color: 'text.disabled', opacity: 0.4 },
            }}
          >
            {digit}
          </ButtonBase>
        ))}

        {/* Backspace */}
        <ButtonBase
          onClick={handleBackspace}
          disabled={code.length === 0 || isVoting}
          sx={{
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(252,129,129,0.1)' : 'rgba(244,67,54,0.06)',
            border: 1, borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(252,129,129,0.2)' : 'rgba(244,67,54,0.15)',
            borderRadius: 3, color: 'error.main', fontSize: '1rem', py: 1.75,
            transition: 'all 0.1s', '&:active': { transform: 'scale(0.96)' },
          }}
        >
          <Backspace sx={{ fontSize: 20 }} />
        </ButtonBase>

        {/* 0 */}
        <ButtonBase
          onClick={() => handleKeyPress('0')}
          disabled={code.length >= digitCount || isVoting}
          sx={{
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : 'background.paper',
            border: 1, borderColor: 'divider', borderRadius: 3, color: 'text.primary',
            fontSize: '1.375rem', fontWeight: 600, py: 1.75, fontFamily: "'Inter', sans-serif",
            transition: 'all 0.1s',
            '&:active': {
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
              transform: 'scale(0.96)',
            },
          }}
        >
          0
        </ButtonBase>

        {/* Submit */}
        <ButtonBase
          onClick={handleSubmit}
          disabled={!canSubmit || isVoting}
          sx={{
            background: canSubmit ? 'linear-gradient(135deg, #d4a853, #c4943f)' : 'rgba(128,128,128,0.15)',
            border: 'none', borderRadius: 3, color: canSubmit ? '#1a1a2e' : 'text.disabled',
            fontSize: '0.875rem', fontWeight: 700, py: 1.75, fontFamily: "'Inter', sans-serif",
            boxShadow: canSubmit ? '0 2px 8px rgba(212,168,83,0.3)' : 'none', transition: 'all 0.15s',
          }}
        >
          {isVoting ? <CircularProgress size={20} sx={{ color: '#1a1a2e' }} /> : 'Submit'}
        </ButtonBase>
      </Box>

      <Typography sx={{ textAlign: 'center', fontSize: '0.6875rem', color: 'text.disabled', mb: 0.5 }}>
        {digitCount} digits required
      </Typography>
    </>
  )
}


// ========================
// Roller Digits Vote Input
// ========================

interface RollerDigitsVoteInputProps {
  digitCount: number
  questionText?: string | null
  isVoting: boolean
  onSubmit: (value: string) => void
}

function RollerDigitsVoteInput({ digitCount, questionText, isVoting, onSubmit }: RollerDigitsVoteInputProps) {
  const [digits, setDigits] = useState<number[]>(() => Array(digitCount).fill(0))
  const [hasInteracted, setHasInteracted] = useState(false)

  const setDigitAt = useCallback((index: number, value: number) => {
    setHasInteracted(true)
    setDigits((prev) => {
      const next = [...prev]
      next[index] = value
      return next
    })
  }, [])

  const handleReset = () => {
    setDigits(Array(digitCount).fill(0))
    setHasInteracted(false)
  }

  const handleSubmit = () => {
    if (!hasInteracted || isVoting) return
    onSubmit(digits.join(''))
  }

  return (
    <>
      {questionText && (
        <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.6, color: 'text.secondary', mb: 1.5 }}>
          {questionText}
        </Typography>
      )}
      <Typography sx={{
        fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.8px', color: 'text.disabled', mb: 0.5,
        display: 'flex', alignItems: 'center', gap: 0.75,
      }}>
        <Lock sx={{ fontSize: 14 }} /> Set the Combination
      </Typography>
      <Typography sx={{ fontSize: '0.6875rem', color: 'text.disabled', mb: 2 }}>
        Swipe each dial up or down
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
        {digits.map((digit, index) => (
          <RollerColumn key={index} index={index} digit={digit} setDigitAt={setDigitAt} disabled={isVoting} />
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 1 }}>
        <Button
          variant="outlined" size="small" startIcon={<RestartAlt />}
          onClick={handleReset} disabled={isVoting}
          sx={{ borderColor: 'divider', color: 'text.secondary', fontSize: '0.8125rem', textTransform: 'none', borderRadius: 2, px: 2 }}
        >
          Reset
        </Button>
        <ButtonBase
          onClick={handleSubmit}
          disabled={!hasInteracted || isVoting}
          sx={{
            background: hasInteracted ? 'linear-gradient(135deg, #d4a853, #c4943f)' : 'rgba(128,128,128,0.15)',
            color: hasInteracted ? '#1a1a2e' : 'text.disabled', borderRadius: 2, px: 3, py: 1,
            fontSize: '0.875rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75,
            boxShadow: hasInteracted ? '0 2px 8px rgba(212,168,83,0.3)' : 'none', transition: 'all 0.15s',
          }}
        >
          {isVoting ? <CircularProgress size={18} sx={{ color: '#1a1a2e' }} /> : <><LockOpen sx={{ fontSize: 18 }} />Unlock</>}
        </ButtonBase>
      </Box>
    </>
  )
}


// ========================
// Roller Column (shared)
// ========================

interface RollerColumnProps {
  index: number
  digit: number
  setDigitAt: (index: number, value: number) => void
  disabled: boolean
}

function RollerColumn({ index, digit, setDigitAt, disabled }: RollerColumnProps) {
  const prevDigit = (digit - 1 + 10) % 10
  const nextDigit = (digit + 1) % 10

  const scrollUp = useCallback(() => {
    if (disabled) return
    setDigitAt(index, (digit - 1 + 10) % 10)
  }, [disabled, setDigitAt, index, digit])

  const scrollDown = useCallback(() => {
    if (disabled) return
    setDigitAt(index, (digit + 1) % 10)
  }, [disabled, setDigitAt, index, digit])

  const onStep = useCallback((dir: 1 | -1) => {
    setDigitAt(index, (digit + dir + 10) % 10)
  }, [setDigitAt, index, digit])

  const { containerRef, handleWheel } = useFlickInertia({ onStep, itemCount: 10, disabled })

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowUp') { e.preventDefault(); scrollUp() }
    else if (e.key === 'ArrowDown') { e.preventDefault(); scrollDown() }
  }, [scrollUp, scrollDown])

  const digitCellSx = {
    fontSize: '1.125rem', fontWeight: 500, fontFamily: "'Inter', sans-serif",
    fontVariantNumeric: 'tabular-nums' as const, opacity: 0.4, py: 0.75,
    cursor: disabled ? 'default' : 'pointer', userSelect: 'none' as const,
    transition: 'opacity 0.15s', color: 'text.primary',
    '&:hover': disabled ? {} : { opacity: 0.6 },
  }

  return (
    <Box
      ref={containerRef} tabIndex={0} role="spinbutton"
      aria-label={`Digit ${index + 1}, current value ${digit}`}
      aria-valuenow={digit} aria-valuemin={0} aria-valuemax={9}
      onKeyDown={handleKeyDown} onWheel={handleWheel}
      sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(21,32,56,0.6)' : 'rgba(0,0,0,0.04)',
        borderRadius: 2.5, width: 52, overflow: 'hidden', outline: 'none',
        '&:focus-visible': { boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}` },
      }}
    >
      <ButtonBase onClick={scrollUp} disabled={disabled} sx={digitCellSx} tabIndex={-1} aria-hidden>
        {prevDigit}
      </ButtonBase>
      <Box sx={{
        fontSize: '1.5rem', fontWeight: 700, fontFamily: "'Inter', sans-serif",
        fontVariantNumeric: 'tabular-nums', color: 'text.primary', py: 1, px: 1.5, mx: 0.5,
        borderTop: 2, borderBottom: 2,
        borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(212,168,83,0.6)' : 'primary.main',
        width: '100%', textAlign: 'center', userSelect: 'none',
      }}>
        {digit}
      </Box>
      <ButtonBase onClick={scrollDown} disabled={disabled} sx={digitCellSx} tabIndex={-1} aria-hidden>
        {nextDigit}
      </ButtonBase>
    </Box>
  )
}


// ========================
// Codex Vote Input
// ========================

interface CodexVoteInputProps {
  challengeData?: Record<string, unknown> | null
  questionText?: string | null
  isVoting: boolean
  onSubmit: (value: string) => void
}

function CodexVoteInput({ challengeData, questionText, isVoting, onSubmit }: CodexVoteInputProps) {
  const letterCount = (challengeData?.letterCount ?? challengeData?.LetterCount ?? 4) as number
  const [letters, setLetters] = useState('')

  const handleSubmit = () => {
    if (letters.length !== letterCount || isVoting) return
    onSubmit(letters.toUpperCase())
  }

  const canSubmit = letters.length === letterCount

  return (
    <>
      {questionText && (
        <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.6, color: 'text.secondary', mb: 1.5 }}>
          {questionText}
        </Typography>
      )}
      <Typography sx={{
        fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.8px', color: 'text.disabled', mb: 1.5,
        display: 'flex', alignItems: 'center', gap: 0.75,
      }}>
        🔤 Enter Code
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box
          component="input"
          type="text"
          value={letters}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value.replace(/[^a-zA-Z]/g, '').slice(0, letterCount).toUpperCase()
            setLetters(val)
          }}
          onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' && canSubmit) handleSubmit() }}
          placeholder={`Enter ${letterCount} letters...`}
          disabled={isVoting}
          maxLength={letterCount}
          sx={{
            flex: 1, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(21,32,58,0.6)' : 'rgba(0,0,0,0.04)',
            border: 1, borderColor: 'divider', borderRadius: '24px', px: 2.5, py: 1.5,
            fontSize: '1.125rem', fontWeight: 600, color: 'text.primary',
            fontFamily: "'Inter', sans-serif", textAlign: 'center', letterSpacing: '4px',
            textTransform: 'uppercase', outline: 'none',
            '&:focus': { borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(212,168,83,0.4)' : 'primary.main' },
            '&::placeholder': { color: 'text.disabled', fontWeight: 400, letterSpacing: 0, fontSize: '0.875rem', textTransform: 'none' },
          }}
        />
        <ButtonBase
          onClick={handleSubmit}
          disabled={!canSubmit || isVoting}
          sx={{
            width: 46, height: 46, borderRadius: '50%',
            background: canSubmit ? 'linear-gradient(135deg, #d4a853, #c4943f)' : 'rgba(128,128,128,0.2)',
            color: canSubmit ? '#1a1a2e' : 'text.disabled', flexShrink: 0,
            boxShadow: canSubmit ? '0 2px 8px rgba(212,168,83,0.3)' : 'none', transition: 'all 0.15s',
          }}
        >
          {isVoting ? <CircularProgress size={20} sx={{ color: '#1a1a2e' }} /> : <Send sx={{ fontSize: 18 }} />}
        </ButtonBase>
      </Box>

      <Typography sx={{ textAlign: 'center', fontSize: '0.6875rem', color: 'text.disabled', mt: 1 }}>
        {letterCount} letters required
      </Typography>
    </>
  )
}
